import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import type { components } from "@/api/types.gen";
import type {
  DataResponse,
  ListResponse,
  Operation,
  OperationCreate,
} from "@/api/schemas";

type OperationUpdate = components["schemas"]["OperationUpdateRequest"];

export function useOperacie(params?: {
  /** true = len kategórie práce (nc…), false = len platené operácie. */
  systemove?: boolean;
}): UseQueryResult<ListResponse<Operation>> {
  const systemove = params?.systemove;
  return useQuery({
    queryKey: [...queryKeys.operacie.list(), systemove ?? "all"],
    queryFn: async () => {
      const { data } = await api.get<ListResponse<Operation>>("/operacie", {
        params: systemove === undefined ? undefined : { systemove },
      });
      return data;
    },
    staleTime: 5 * 60 * 1000, // operácie sa menia zriedka
  });
}

export function useCreateOperacia(): UseMutationResult<
  DataResponse<Operation>,
  Error,
  OperationCreate
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: OperationCreate) => {
      const { data } = await api.post<DataResponse<Operation>>(
        "/operacie",
        payload,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.operacie.all });
    },
  });
}

export function useDeleteOperacia(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (kod: string) => {
      await api.delete(`/operacie/${kod}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.operacie.all });
    },
  });
}

export function useUpdateOperacia(): UseMutationResult<
  DataResponse<Operation>,
  Error,
  { kod: string; data: OperationUpdate }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ kod, data }) => {
      const response = await api.patch<DataResponse<Operation>>(
        `/operacie/${kod}`,
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.operacie.all });
    },
  });
}
